import AppsDropdown from '@proton/components/containers/app/AppsDropdown'
import Logo from '@proton/components/components/logo/Logo'
import MimeIcon from '@proton/components/components/icon/MimeIcon'
import useActiveBreakpoint from '@proton/components/hooks/useActiveBreakpoint'
import { useConfig } from '@proton/app-context/useConfig'
import UserDropdown from '@proton/components/containers/heading/UserDropdown'
import { IcEye } from '@proton/icons/icons/IcEye'
import { IcLockFilled } from '@proton/icons/icons/IcLockFilled'
import { APPS } from '@proton/shared/lib/constants'
import { DocumentTitleDropdown } from './DocumentTitleDropdown/DocumentTitleDropdown'
import { DocumentActiveUsers } from './DocumentActiveUsers'
import { ConnectionStatus } from './ConnectionStatus'
import { useEffect, useState } from 'react'
import { useApplication } from '~/utils/application-context'
import { traceError } from '@proton/shared/lib/helpers/sentry'
import { CommentsButton } from './CommentsButton'
import { c } from 'ttag'
import { PopoverPill } from './PopoverPill'
import { useDocsContext } from '../../context'
import { HeaderPublicOptions } from '../../public/HeaderPublicOptions'
import type {
  EditorControllerInterface,
  AuthenticatedDocControllerInterface,
  DocumentState,
  PublicDocumentState,
  RenameControllerInterface,
} from '@proton/docs-core'
import { stripLocalBasenameFromPathname } from '@proton/shared/lib/authentication/pathnameHelper'
import type { DocumentAction, DocumentType } from '@proton/docs-shared'
import { getAppHref } from '@proton/shared/lib/apps/helper'
import { useFlag } from '@proton/unleash/useFlag'
import clsx from '@proton/utils/clsx'
import { useIsSheetsEditorEnabled, useSharingModalDriveSdkEnabled } from '~/utils/flags'
import { getDocsReportContextLines } from '~/utils/report-context'
import { useSharingModal } from '@proton/drive/public/sharingModal'
import { WorkspacePromoBanner } from '../../DocumentViewer/WorkspacePromoBanner'
import { HeaderShareButton } from './HeaderShareButton'
import { generateNodeUid, getDrive } from '@proton/drive'
import { replaceAddress, reportChangeAddressError } from '../../useChangeAddressWhenPubliclyShared'
import useAuthentication from '@proton/components/hooks/useAuthentication'

function getWindowLocationExcludingDomain() {
  return stripLocalBasenameFromPathname(window.location.pathname) + window.location.search + window.location.hash
}

export type DocsHeaderProps = { actionMode?: DocumentAction['mode']; documentType: DocumentType }

export function DocumentHeader({ actionMode, documentType }: DocsHeaderProps) {
  const application = useApplication()

  const [isReady, setIsReady] = useState(false)

  const [authenticatedController, setAuthenticatedController] = useState<
    AuthenticatedDocControllerInterface | undefined
  >(undefined)
  const [renameController, setRenameController] = useState<RenameControllerInterface>()
  const [editorController, setEditorController] = useState<EditorControllerInterface | null>(null)
  const [documentState, setDocumentState] = useState<DocumentState | PublicDocumentState | null>(null)
  const [showErrorHeader, setShowErrorHeader] = useState(false)
  const isSheetsEditorEnabled = useIsSheetsEditorEnabled()

  useEffect(() => {
    return application.userState.subscribeToEvent('BlockingInterfaceErrorDidDisplay', () => {
      setShowErrorHeader(true)
    })
  }, [application.userState])

  useEffect(() => {
    return application.getDocLoader().addStatusObserver({
      onSuccess: (result) => {
        setIsReady(true)
        setAuthenticatedController(result.docController)
        setEditorController(result.editorController)
        setDocumentState(result.documentState)
        setRenameController(result.renameController)
      },
      onError: (error) => {
        traceError(error)
      },
    })
  }, [application])

  if (documentType === 'sheet' && !isSheetsEditorEnabled) {
    return <DocsHeaderNoDocument />
  }

  if (application.isRunningInNativeMobileWeb) {
    return null
  }

  if (isReady && editorController && documentState) {
    return (
      <DocsHeaderForDocument
        documentType={documentType}
        actionMode={actionMode}
        editorController={editorController}
        documentState={documentState}
        authenticatedController={authenticatedController}
        renameController={renameController}
      />
    )
  }

  if (showErrorHeader) {
    return <DocsHeaderNoDocument />
  }

  return null
}

/**
 * Header shown if there is an error loading a document.
 */
function DocsHeaderNoDocument() {
  const { viewportWidth } = useActiveBreakpoint()
  const { APP_VERSION, CLIENT_TYPE } = useConfig()

  return (
    <div className="error-header flex flex-nowrap items-center justify-between gap-2 bg-signalInfoMinorCustom px-10 pt-5">
      <div className="flex flex-shrink-0 flex-nowrap items-center gap-2">
        <Logo variant={viewportWidth['<=small'] ? 'glyph-only' : 'with-wordmark'} appName={APPS.PROTONDOCS} />
        <AppsDropdown />
      </div>
      <div className="hidden items-center justify-center text-center md:flex">
        <IcLockFilled className="mr-1.5" />
        <span className="pt-0.5 text-sm">{c('Title').t`End-to-end encrypted`}</span>
      </div>
      <div className="no-doc-header">
        <UserDropdown
          app={APPS.PROTONDOCS}
          reportDescriptionContext={getDocsReportContextLines({
            appVersion: APP_VERSION,
            clientType: CLIENT_TYPE,
          })}
          sessionOptions={{
            path: getWindowLocationExcludingDomain(),
            target: '_self',
          }}
        />
      </div>
    </div>
  )
}

type DocsHeaderForDocumentProps = {
  actionMode?: DocumentAction['mode']
  editorController: EditorControllerInterface
  documentState: DocumentState | PublicDocumentState
  authenticatedController: AuthenticatedDocControllerInterface | undefined
  renameController: RenameControllerInterface | undefined
  documentType: DocumentType
}

/**
 * Header shown while a document is present.
 */
function DocsHeaderForDocument({
  actionMode,
  editorController,
  documentState,
  authenticatedController,
  renameController,
  documentType,
}: DocsHeaderForDocumentProps) {
  const { publicContext } = useDocsContext()
  const { APP_VERSION, CLIENT_TYPE } = useConfig()
  const isHomepageEnabled = useFlag('DocsHomepageEnabled')
  const { getLocalID } = useAuthentication()

  const sharingModalDriveSdkEnabled = useSharingModalDriveSdkEnabled()
  const { showSharingModal, sharingModal } = useSharingModal()
  function openSharingModalReplaceAddressSDK() {
    showSharingModal({
      drive: getDrive(),
      nodeUid: generateNodeUid(volumeId, nodeId),
      onShareSnapshot(result) {
        if (result.ok) {
          const { urlAccess } = result.value
          try {
            replaceAddress({ getLocalID, urlAccess, volumeId, nodeId })
          } catch (error: any) {
            reportChangeAddressError(error, {
              volumeId,
              nodeId,
            })
          }
        }
      },
    })
  }

  const role = documentState.getProperty('userRole')
  const { volumeId, nodeId } = documentState.getProperty('decryptedNode')

  const icon = (
    <MimeIcon
      name={documentType === 'sheet' ? 'proton-sheet' : 'proton-doc'}
      size={5}
      className="ml-[.75rem] mr-[.0625rem] shrink-0"
    />
  )

  return (
    <>
      {!publicContext && <WorkspacePromoBanner />}
      <div
        className={clsx(
          'flex select-none flex-nowrap items-center gap-2 px-3 py-2',
          documentType === 'sheet' && 'bg-[#F9FBFC] [[data-theme-mode=dark]_&]:!bg-[--background-norm]',
        )}
        data-testid="docs-header"
      >
        <div className="flex flex-1 flex-nowrap items-center head-480-749:!flex-none head-max-479:!basis-auto">
          {isHomepageEnabled ? (
            <a className="flex-shrink-0" href={getAppHref('/', APPS.PROTONDOCS)}>
              {icon}
            </a>
          ) : (
            icon
          )}

          <DocumentTitleDropdown
            documentType={documentType}
            actionMode={actionMode}
            authenticatedController={authenticatedController}
            editorController={editorController}
            documentState={documentState}
            renameController={renameController}
          />
          <div
            className="flex-grow-1 flex-basis-0 ml-0.5 flex min-w-fit flex-shrink-0 items-center justify-between gap-2 head-max-1199:!max-w-[4.5rem]"
            data-testid="status-container"
          >
            <ConnectionStatus documentState={documentState} />
            <div className="flex-none head-max-479:![display:none]">{!role.canEdit() && <ViewOnlyPill />}</div>
          </div>
        </div>

        <div className="flex flex-1 flex-nowrap items-center justify-end head-max-479:!flex-shrink-0 head-max-479:!basis-auto">
          {publicContext ? (
            <HeaderPublicOptions
              editorController={editorController}
              documentState={documentState as PublicDocumentState}
              documentType={documentType}
            />
          ) : (
            <>
              <DocumentActiveUsers className="mr-2 hidden md:flex" />

              {documentState.getProperty('userRole').canShare() && (
                <HeaderShareButton
                  onClick={
                    sharingModalDriveSdkEnabled
                      ? openSharingModalReplaceAddressSDK
                      : () => authenticatedController?.openDocumentSharingModal()
                  }
                />
              )}
            </>
          )}

          {!publicContext && (
            <>
              {documentState.getProperty('userRole').canComment() && documentType !== 'sheet' && (
                <CommentsButton editorController={editorController} />
              )}
              <div className="w-2" />
              <UserDropdown
                app={APPS.PROTONDOCS}
                reportDescriptionContext={getDocsReportContextLines({
                  documentType,
                  role,
                  appVersion: APP_VERSION,
                  clientType: CLIENT_TYPE,
                })}
                sessionOptions={{ path: getWindowLocationExcludingDomain(), target: '_self' }}
              />
            </>
          )}
        </div>
      </div>

      {sharingModal}
    </>
  )
}

function ViewOnlyPill() {
  return (
    <PopoverPill
      placement="bottom"
      title={
        <div className="flex gap-2" data-testid="changes-info-e2e-encrypted">
          <IcEye className="h-6 w-6 fill-current" />
          <span>{c('Info').t`View only`}</span>
        </div>
      }
      content={c('Info')
        .t`You are currently viewing this document in view-only mode. Reach out to the owner to request access to edit and comment.`}
    >
      <IcEye className="h-4 w-4 fill-current" />
      <span className="head-max-1199:sr-only">{c('Info').t`View only`}</span>
    </PopoverPill>
  )
}
